import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@drpcrd/utils/shared";
import { ButtonHTMLAttributes, ElementType, forwardRef } from "react";
import { LucideIcon } from "lucide-react";
import { CustomSpinner } from "../icons/custom-spinner";

const buttonVariants = cva(
  cn(
    // Base styles
    "inline-flex align-middle h-auto items-center justify-center font-semibold rounded-full cursor-pointer focus-visible:outline-hidden transition-all ring-1 ring-inset",
    // Disabled styles
    "disabled:bg-neutral-500 disabled:ring-neutral-500 disabled:text-neutral-900 disabled:pointer-events-none",
  ),
  {
    variants: {
      variant: {
        filled: "ring-primary bg-primary hover:bg-primary-600 text-white",
        outlined: "ring-primary hover:bg-primary-50 text-primary",
        error: "ring-pink-500 bg-pink-500 hover:bg-pink-600 text-white",
        gradient:
          "bg-[linear-gradient(136deg,_#763FF9_0%,_#FF0174_100%)] text-white",
        text: "hover:underline px-0! font-normal rounded-none ring-0! disabled:bg-transparent!",
      },
      size: {
        sm: "px-1.5 py-1   text-xs leading-[14px] gap-0.75 [&>svg]:w-1.5 [&>svg]:h-1.5 [&>svg]:stroke-2",
        md: "px-2   py-1   text-sm leading-2.75 gap-1    [&>svg]:w-2   [&>svg]:h-2   [&>svg]:stroke-[2.25]",
        lg: "px-2.5 py-1.5 text-lg leading-[24px] gap-1.5  [&>svg]:w-3   [&>svg]:h-3   [&>svg]:stroke-[2.75]",
      },
    },
    defaultVariants: {
      variant: "filled",
      size: "md",
    },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    icon?: LucideIcon;
    as?: ElementType;
    href?: string;
    target?: string;
    iconPosition?: "start" | "end";
    iconClassName?: string;
    loading?: boolean;
  };

/**
 * The one button to rule them all! Use this button for all buttons across all
 * apps. The button supports different variants, sizes, icons, loading states,
 * and can be rendered as different elements (e.g. `a`, `button`, `Link`).
 *
 * @default
 * `button` component
 *
 * @default
 * `variant="filled"`
 * `size="md"`
 *
 * @example
 * To render a Button with an onClick handler, simply use the `onClick` prop:
 * ```tsx
 * <Button onClick={() => console.log('Button clicked!')}>Click me</Button>
 * ```
 * @example
 * To render a Button as a link, use the `as` prop to specify the
 * element type. Don't wrap the Button in a Link component, as this can cause
 * accessibility issues. Instead, do the following:
 * ```tsx
 * import Link from 'next/link';
 *
 * <Button as={Link} href="/about">Go to About</Button>
 * ```
 *
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      icon: Icon,
      children,
      iconPosition = "start",
      iconClassName,
      loading = false,
      as,
      ...props
    },
    ref,
  ) => {
    if (props.href && !as) {
      // This means the developer has not set `as` to `Link` but has provided an `href` prop
      if (process.env.NODE_ENV !== "production") {
        throw new Error(
          "You must set the `as` prop to Next.js `Link` when providing an `href` prop to Button",
        );
      }
    }

    const Comp = as ?? "button";

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading || props.disabled}
        {...props}
      >
        {loading && iconPosition === "start" && (
          <CustomSpinner className="animate-spin" />
        )}
        {!loading && iconPosition === "start" && Icon && (
          <Icon className={iconClassName} />
        )}
        {children}
        {!loading && iconPosition === "end" && Icon && (
          <Icon className={iconClassName} />
        )}
        {loading && iconPosition === "end" && (
          <CustomSpinner className="animate-spin" />
        )}
      </Comp>
    );
  },
);

Button.displayName = "Button";
